<template>
  <div class="search-bar__date">
    <div
      v-if="dateFilterAlternative"
      class="alternative"
      :class="{ 'cleaned-datepicker': clearnedDatepicker }"
    >
      <v-date-picker
        ref="popover"
        :value="dateSelectedRoot"
        :popover-direction="calendarDirection"
        :date-picker-show-caps="false"
        :date-picker-show-day-popover="false"
        :pane-width="290"
        :tint-color="'#7f1a54'"
        :max-date="maxDate"
        :min-date="limitData"
        mode="range"
        title
        maxlength="23"
        name="dateFilter"
        show-caps
        expanded
        @drag="onDragDate"
        @dayclick="onDateClick"
        @input="onDateChange"
      >
        <md-input-container
          id="datePickerSelect"
          slot-scope="{ inputValue, updateValue }"
        >
          <md-input
            id="dateFilter"
            ref="dateFilter"
            :readonly="false"
            type="text"
            :value="inputValue"
            expanded
            @change.native="updateTypedValue($event.target.value, updateValue)"
            @keyup.enter.native.stop="onEnterPressed($event, updateValue)"
          />
          <div
            v-if="!clearnedDatepicker && clearButton"
            class="clear-button"
            @click.stop="clearDates()"
          >
            <em class="clear-button__icon" />
          </div>
          <md-icon :md-src="'/static/images/icon-calendar-alternative.svg'">date_range</md-icon>
        </md-input-container>
      </v-date-picker>
    </div>
  </div>
</template>

<script>
import Moment from 'moment';

export default {
  name: 'DatePickerRange',
  props: {
    dateSelected: {
      type: Object,
      default: () => ({
        start: null,
        end: null,
      }),
    },
    dateFilterAlternative: {
      type: Boolean,
      default: false,
    },
    calendarDirection: {
      type: String,
      default: 'bottom',
    },
    clearButton: {
      type: Boolean,
      default: true,
    },
    // Existing report forms initialize their payload from this first update.
    // Search forms already own their initial state and opt out of it.
    emitInitialValue: {
      type: Boolean,
      default: true,
    },
    maxDate: {
      type: Date,
      default: () => new Date(),
    },
    limitData: Date,
    defaultStartDate: [String, Date],
    defaultEndDate: [String, Date],
  },
  data: () => ({
    clearnedDatepicker: false,
    dateSelectedRoot: {
      start: null,
      end: null,
    },
  }),
  watch: {
    dateSelected: {
      deep: true,
      handler(value) {
        this.hydrateDates(value);
      },
    },
  },
  created() {
    const selected = this.normalizeRange(this.dateSelected);
    const defaults = this.normalizeRange({ start: this.defaultStartDate, end: this.defaultEndDate });
    const explicitlyCleared = this.$options.propsData && this.$options.propsData.dateSelected &&
      this.dateSelected.start === null && this.dateSelected.end === null;
    this.hydrateDates(explicitlyCleared ? null : selected || defaults);
  },
  mounted() {
    this.updateCustomLabel();
    if (this.emitInitialValue && this.dateSelectedRoot.start && this.dateSelectedRoot.end) {
      this.$emit('update:dateSelected', { ...this.dateSelectedRoot });
    }
  },
  updated() {
    this.updateCustomLabel();
  },
  methods: {
    normalizeDate(value) {
      if (!value) return null;
      const date = typeof value === 'string'
        ? Moment(value, ['DD/MM/YYYY', Moment.ISO_8601], true)
        : Moment(value);
      return date.isValid() ? date.startOf('day').toDate() : null;
    },
    normalizeRange(value) {
      if (!value) return null;
      const start = this.normalizeDate(value.start);
      const end = this.normalizeDate(value.end);
      return start && end && start <= end ? { start, end } : null;
    },
    rangesEqual(first, second) {
      return ['start', 'end'].every(key => {
        const firstDate = this.normalizeDate(first && first[key]);
        const secondDate = this.normalizeDate(second && second[key]);
        return (firstDate && firstDate.getTime()) === (secondDate && secondDate.getTime());
      });
    },
    hydrateDates(value) {
      const range = this.normalizeRange(value) || { start: null, end: null };
      if (!this.rangesEqual(this.dateSelectedRoot, range)) {
        this.dateSelectedRoot = range;
      }
      this.clearnedDatepicker = !range.start || !range.end;
      this.updateCustomLabel();
    },
    clearDates() {
      if (this.$refs.dateFilter) this.$refs.dateFilter.setParentPlaceholder();
      if (this.$refs.popover) this.$refs.popover.hidePopover();
      this.onDateChange(null);
    },
    onDateClick(value) {
      this.$emit('handleSelectFirstDate', value.date);
    },
    onDragDate() {
      this.clearnedDatepicker = false;
    },
    onDateChange(value) {
      const range = this.normalizeRange(value);
      // A partially selected or invalid range must not replace the last search.
      if (value && (value.start || value.end) && !range) return;
      const nextRange = range || { start: null, end: null };
      if (this.rangesEqual(this.dateSelectedRoot, nextRange)) return;
      this.dateSelectedRoot = nextRange;
      this.clearnedDatepicker = !range;
      this.$emit('update:dateSelected', { ...nextRange });
      this.updateCustomLabel();
    },
    updateTypedValue(value, updateValue) {
      const text = value.trim();
      // The display label is not a date understood by v-calendar's parser.
      if (text === 'Hoje' && this.dateSelectedRoot.start && this.dateSelectedRoot.end) return true;
      if (!text) {
        updateValue(null);
        return true;
      }
      const dates = text.split(/\s*-\s*/);
      const range = dates.length === 2 && this.normalizeRange({ start: dates[0], end: dates[1] });
      if (!range) return false;
      // v-calendar applies valid, enabled dates synchronously and rejects disabled ones.
      updateValue(text);
      return this.rangesEqual(this.dateSelectedRoot, range);
    },
    onEnterPressed(event, updateValue) {
      if (this.updateTypedValue(event.target.value, updateValue)) {
        this.$nextTick(() => this.$emit('enterPressed'));
      }
    },
    updateCustomLabel() {
      this.$nextTick(() => {
        if (!this.$refs.dateFilter || !this.$refs.dateFilter.$el) return;
        const today = Moment().format('DD/MM/YYYY');
        const start = Moment(this.dateSelectedRoot.start).format('DD/MM/YYYY');
        const end = Moment(this.dateSelectedRoot.end).format('DD/MM/YYYY');
        if (start === today && end === today) {
          this.$refs.dateFilter.$el.value = 'Hoje';
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import '../../../static/stylesheets/variables';

.clear-button {
  margin: auto;

  em {
    padding: 3px 4px 1px 4px;
    font-size: 11px;
    border-radius: 5px;
    margin-left: 0px;
    position: relative;
    margin: auto;
    cursor: pointer;

    &:hover {
      background: $brand-primary;
      color: white;
    }
  }
}

.clear-button__icon {
  cursor: pointer;
  margin-left: 7px;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  font-weight: 700;
  font-style: normal;
  width: 22px;
  text-align: center;
  line-height: 22px;
  transition: all 0.2s ease;
  border-radius: 5px;

  &::after {
    content: '\D7';
    color: #266d4d;
    font-size: 14px;
  }

  &:hover::after {
    color: white;
  }
}

.cleaned-datepicker {
  .c-day-content-wrapper {
    background: #fafafa;
  }

  .c-day-content {
    color: #393d46 !important;
  }

  .days-nights {
    display: none;
  }
}

.days-nights {
  width: 100px !important;
}

.alternative {
  .md-input-container {
    margin-top: 0;
    min-height: 50px;

    &::after {
      display: none !important;
    }
  }
}

.search-bar {
  &__date {
    margin: 0 10px;
    flex: 2;
    position: relative;

    input {
      cursor: pointer;
    }

    &__button {
      &:not([disabled]) {
        &:not(.md-raised) {
          height: 33px;
          min-height: 33px;
          width: calc(100% - 32px);
          margin: 0;
          padding: 0;
          font-size: 15px;
          color: #717171;
          font-weight: 400;
          text-align: left;
          text-transform: lowercase;
          background: transparent;
          margin-top: -1px;

          @supports (-ms-ime-align: auto) {
            color: #222222 !important;
          }

          &:hover,
          &:active {
            &:not([disabled]):not(.md-raised) {
              background: none;
            }
          }

          .md-ink-ripple {
            display: none;
          }
        }
      }
    }

    &__icon {
      cursor: pointer;
      position: absolute;
      top: 23px;
      right: 0;
    }

    .md-input-container {
      label {
        opacity: 1;
        top: 22px;
        font-size: 15px;
        color: #717171;

        @supports (-ms-ime-align: auto) {
          color: #222222 !important;
        }
      }

      &.md-input-focused {
        label {
          opacity: 1;
          font-size: 15px;
          color: #717171 !important;

          @supports (-ms-ime-align: auto) {
            color: #222222 !important;
          }
        }
      }

      &.md-has-value {
        label {
          top: 0;
          font-size: 12px;
          color: #858585 !important;
        }

        .md-input {
          font-size: 15px;
          letter-spacing: -0.2px;
          color: #717171;

          @supports (-ms-ime-align: auto) {
            color: #222222 !important;
          }
        }
      }

      &.md-input-invalid {
        label {
          color: #717171 !important;

          @supports (-ms-ime-align: auto) {
            color: #222222 !important;
          }
        }

        &.md-has-value {
          label {
            color: #858585 !important;
          }
        }
      }
    }
  }
}
</style>
